/**
 * How the chatbot is rendered on the integrating page:
 * - `floating` (default): a bubble in a viewport corner that opens the chat window.
 * - `embedded`: the chat window rendered inline, filling the #chatbot-container
 *   element, always open, like the help client.
 */
export type ChatbotDisplayMode = 'floating' | 'embedded';

/**
 * Normalizes a display mode from the snippet attribute or the JS config.
 *
 * @param value raw value (case and surrounding whitespace are ignored)
 * @returns the display mode, or undefined when unset or unrecognized
 */
export function normalizeChatbotDisplayMode(value: unknown): ChatbotDisplayMode | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'floating' || normalized === 'embedded') {
    return normalized;
  }
  return undefined;
}

/**
 * Picks the display mode: the JS config wins over the snippet attribute, an
 * unrecognized value is skipped with a console warning, floating is the default.
 *
 * @param configValue value from initializeChatbot({ displayMode })
 * @param attributeValue value of the data-omq-chatbot-display-mode attribute
 * @returns the display mode to render
 */
export function resolveChatbotDisplayMode(
  configValue: unknown,
  attributeValue: unknown,
): ChatbotDisplayMode {
  for (const value of [configValue, attributeValue]) {
    const mode = normalizeChatbotDisplayMode(value);
    if (mode) {
      return mode;
    }
    if (value != null && value !== '') {
      console.warn(`[Chatbot] Ignoring unknown displayMode "${String(value)}"`);
    }
  }
  return 'floating';
}
